import "@/App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { Inbox as InboxIcon, Settings2, LayoutDashboard, Camera, MapPin, Table2, SlidersHorizontal } from "lucide-react";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { api } from "./lib/api";
import { BrandBlock, Footer } from "./components/Brand";
import { AccountMenu } from "./components/AccountMenu";
import { ZapinBanner } from "./components/ZapinBanner";
import LoginPage from "./pages/Login";

const InboxPage = lazy(() => import("./pages/Inbox"));
const SetupPage = lazy(() => import("./pages/Setup"));
const DashboardPage = lazy(() => import("./pages/Dashboard"));
const CamerasPage = lazy(() => import("./pages/Cameras"));
const SitesPage = lazy(() => import("./pages/Sites"));
const DataPage = lazy(() => import("./pages/Data"));
const SettingsPage = lazy(() => import("./pages/Settings"));

const NAV = [
  ["/dashboard", LayoutDashboard, "Dashboard", "nav-dashboard", "user"],
  ["/cameras", Camera, "Cameras", "nav-cameras", "user"],
  ["/sites", MapPin, "Sites", "nav-sites", "admin"],
  ["/data", Table2, "Data", "nav-data", "user"],
  ["/inbox", InboxIcon, "Inbox", "nav-inbox", "admin"],
  ["/setup", Settings2, "Setup", "nav-setup", "admin"],
  ["/settings", SlidersHorizontal, "Settings", "nav-settings", "admin"],
];
const ADMIN_PATHS = NAV.filter((n) => n[4] === "admin").map((n) => n[0]);

const Shell = ({ children }) => {
  const { user } = useAuth();
  const [offline, setOffline] = useState(0);
  useEffect(() => {
    const load = () => api.camerasStatus().then((cs) => setOffline(cs.filter((c) => c.active !== false && !c.isSimulated && c.status === "offline").length)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="shell">
      <aside className="sidebar" data-testid="sidebar">
        <div className="brand" data-testid="brand-legacy" style={{ display: "none" }} />
        <BrandBlock size={20} />
        <nav className="side-nav">
          {NAV.filter(([, , , , role]) => role === "user" || user?.role === "admin").map(([to, Icon, label, tid]) => (
            <NavLink key={to} to={to} data-testid={tid} className={({ isActive }) => `side-item ${isActive ? "side-item-active" : ""}`}>
              <Icon size={17} strokeWidth={2} /><span>{label}</span>
              {to === "/cameras" && offline > 0 && <span className="nav-badge" data-testid="offline-badge" title={`${offline} camera(s) offline`}>{offline}</span>}
            </NavLink>
          ))}
        </nav>
        <AccountMenu />
      </aside>
      <main className="page"><ZapinBanner /><Suspense fallback={<div className="center-screen muted" data-testid="route-loading">Loading…</div>}>{children}</Suspense><Footer /></main>
    </div>
  );
};

const Protected = ({ children }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="center-screen muted" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (user.role !== "admin" && ADMIN_PATHS.some((p) => loc.pathname.startsWith(p))) {
    toast.error("That page is for administrators");
    return <Navigate to="/dashboard" replace />;
  }
  return <Shell>{children}</Shell>;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
          <Route path="/cameras" element={<Protected><CamerasPage /></Protected>} />
          <Route path="/sites" element={<Protected><SitesPage /></Protected>} />
          <Route path="/data" element={<Protected><DataPage /></Protected>} />
          <Route path="/inbox" element={<Protected><InboxPage /></Protected>} />
          <Route path="/setup" element={<Protected><SetupPage /></Protected>} />
          <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
